import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

/** Hardcoded project DSN. */
const SENTRY_DSN =
  "https://ab0ef637813fc5ba55b89a25332d6bd4@o4511202926198784.ingest.us.sentry.io/4511652226727941";

let sentryInitialized = false;

/** Call once before React renders (see index.tsx). */
export function initSentry(): void {
  if (sentryInitialized) {
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.REACT_APP_BRANCH || process.env.NODE_ENV,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    // Performance: sample more in dev, less in prod.
    tracesSampleRate: process.env.NODE_ENV === "production" ? 0.2 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });

  sentryInitialized = true;
}

/** Attach interview-scoped tags/context (token, question) for Sentry events. */
export function setInterviewSentryContext(context: {
  token?: string;
  questionId?: number;
  candidateName?: string;
  videoBased?: boolean;
}): void {
  if (!sentryInitialized) return;

  Sentry.setTag("feature", "interview");
  if (context.token) {
    Sentry.setTag("interview.token_prefix", context.token.slice(0, 8));
    Sentry.setContext("interview_session", {
      token_prefix: context.token.slice(0, 8),
      video_based: context.videoBased,
      candidate_name: context.candidateName,
    });
  }
  if (context.questionId !== undefined) {
    Sentry.setTag("interview.question_id", String(context.questionId));
  }
}

/**
 * Forward interview logger events to Sentry:
 *  - info  -> breadcrumb (trail for debugging redirects / flow)
 *  - warn  -> warning message
 *  - error -> exception if present, else error message
 */
export function reportInterviewToSentry(
  level: "info" | "warn" | "error",
  event: string,
  data?: unknown
): void {
  if (!sentryInitialized) return;

  const payload =
    data !== undefined && data !== null && typeof data === "object" && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : data !== undefined
        ? { value: data }
        : undefined;

  Sentry.withScope((scope) => {
    scope.setTag("feature", "interview");
    scope.setTag("interview.event", event);

    if (payload) {
      const questionId = payload.question_id ?? payload.questionId;
      const token = payload.token ?? payload.interview_token;
      if (token) scope.setTag("interview.token_prefix", String(token).slice(0, 8));
      if (questionId !== undefined) scope.setTag("interview.question_id", String(questionId));
      scope.setContext("interview_details", payload);
    }

    if (level === "info") {
      Sentry.addBreadcrumb({
        category: "interview",
        message: event,
        level: "info",
        data: payload as Record<string, unknown> | undefined,
      });
      return;
    }

    const maybeError = payload?.error;
    if (level === "error" && maybeError instanceof Error) {
      Sentry.captureException(maybeError);
      return;
    }

    Sentry.captureMessage(`[Interview] ${event}`, {
      level: level === "warn" ? "warning" : "error",
      extra: payload,
    });
  });
}

export { Sentry };
